<template>
  <div
    class="modal"
    :class="{ 'is-active': modelValue }"
  >
    <div class="modal-background" />
    <div class="modal-card apple-relay-email-modal">
      <header class="modal-card-head">
        <p class="modal-card-title">
          <span class="icon info-icon">
            <i class="fas fa-envelope" />
          </span>
          {{ $t('account.appleRelay.title') }}
        </p>
      </header>

      <section class="modal-card-body">
        <div class="relay-content">
          <p class="description">
            {{ $t('account.appleRelay.description') }}
          </p>

          <div class="reasons-list">
            <div class="reason-item">
              <span class="icon">
                <i class="fas fa-bell" />
              </span>
              <span class="reason-text">{{ $t('account.appleRelay.reasonNotifications') }}</span>
            </div>
            <div class="reason-item">
              <span class="icon">
                <i class="fas fa-shield-alt" />
              </span>
              <span class="reason-text">{{ $t('account.appleRelay.reasonRecovery') }}</span>
            </div>
            <div class="reason-item">
              <span class="icon">
                <i class="fas fa-chart-line" />
              </span>
              <span class="reason-text">{{ $t('account.appleRelay.reasonUpdates') }}</span>
            </div>
          </div>

          <form @submit.prevent="handleSubmit">
            <div class="field">
              <label class="label">{{ $t('account.appleRelay.newEmailLabel') }}</label>
              <div class="control has-icons-left">
                <input
                  v-model="newEmail"
                  class="input"
                  :class="{ 'is-danger': emailError }"
                  type="email"
                  :placeholder="$t('account.appleRelay.newEmailPlaceholder')"
                  autofocus
                >
                <span class="icon is-small is-left">
                  <i class="fas fa-envelope" />
                </span>
              </div>
              <p
                v-if="emailError"
                class="help is-danger"
              >
                {{ emailError }}
              </p>
            </div>
          </form>
        </div>
      </section>

      <footer class="modal-card-foot">
        <button
          class="button is-primary"
          :class="{ 'is-loading': isSubmitting }"
          :disabled="isSubmitting || !newEmail"
          @click="handleSubmit"
        >
          {{ $t('account.appleRelay.updateEmail') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import userService from '@/services/userService';
import { useAuthStore } from '@/store/auth';
import notificationService from '@/services/notificationService';

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'email-updated']);

const { t } = useI18n();
const authStore = useAuthStore();

const newEmail = ref('');
const emailError = ref('');
const isSubmitting = ref(false);

const validateEmail = () => {
  emailError.value = '';

  if (!newEmail.value) {
    emailError.value = t('validation.required', { field: t('account.email') });
    return false;
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.value)) {
    emailError.value = t('errors.email-invalid');
    return false;
  }

  if (newEmail.value.includes('privaterelay.appleid.com')) {
    emailError.value = t('account.appleRelay.cannotUseRelay');
    return false;
  }

  return true;
};

const handleSubmit = async () => {
  if (!validateEmail()) return;

  isSubmitting.value = true;
  emailError.value = '';

  try {
    await userService.update({ email: newEmail.value });
    await authStore.verifySession();

    notificationService.success({
      title: t('account.success'),
      message: t('account.appleRelay.emailUpdated'),
    });

    emit('email-updated');
    emit('update:modelValue', false);
  } catch (error) {
    if (error.response?.data?.name === 'duplicated_email' ||
        error.response?.status === 409) {
      emailError.value = t('account.appleRelay.emailDuplicate');
    } else {
      emailError.value = t('account.update-failed');
    }
  } finally {
    isSubmitting.value = false;
  }
};

</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/breakpoints";

.apple-relay-email-modal {
  width: 90%;
  max-width: 520px;
  border-radius: 16px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;

  .modal-card-head {
    background: linear-gradient(135deg, #3e8ed0 0%, #4a90e2 100%);
    border-bottom: none;
    padding: 1.5rem;

    .modal-card-title {
      display: flex;
      align-items: center;
      font-size: 1.3rem;
      margin: 0;
      color: white;
      -webkit-text-fill-color: white;

      .info-icon {
        margin-right: 0.75rem;
        color: rgba(255, 255, 255, 0.85);
        font-size: 1.2rem;
      }
    }
  }

  .modal-card-body {
    padding: 2rem;
    background: linear-gradient(to bottom, #fff, #f9f9f9);
    flex-grow: 1;

    .body--dark & {
      background: linear-gradient(to bottom, #1a202c, #171923);
    }

    .relay-content {
      .description {
        font-size: 1.05rem;
        margin-bottom: 1.5rem;
        line-height: 1.6;
        color: #333;
        font-weight: 500;

        .body--dark & {
          color: #e2e8f0;
        }
      }

      .reasons-list {
        margin-bottom: 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        .reason-item {
          display: flex;
          align-items: center;
          padding: 0.75rem 1rem;
          background: rgba(62, 142, 208, 0.06);
          border-radius: 8px;
          border-left: 3px solid #3e8ed0;
          color: #2d3748;

          .body--dark & {
            background: rgba(62, 142, 208, 0.1);
            color: #e2e8f0;
            border-left-color: #4a5af8;
          }

          .icon {
            margin-right: 0.75rem;
            color: #3e8ed0;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.9rem;
            flex-shrink: 0;

            .body--dark & {
              color: #4a5af8;
            }
          }

          .reason-text {
            font-size: 0.95rem;
            line-height: 1.4;
          }
        }
      }

      .field {
        .label {
          color: #2d3748;
          font-weight: 600;
          margin-bottom: 0.5rem;

          .body--dark & {
            color: #e2e8f0;
          }
        }

        .input {
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem 0.75rem 2.5rem;
          font-size: 1rem;

          &:focus {
            border-color: #3e8ed0;
            box-shadow: 0 0 0 2px rgba(62, 142, 208, 0.2);
          }

          .body--dark & {
            background-color: #2d3748;
            border-color: #4a5568;
            color: #e2e8f0;

            &:focus {
              border-color: #4a5af8;
              box-shadow: 0 0 0 2px rgba(74, 90, 248, 0.2);
            }
          }
        }
      }
    }
  }

  .modal-card-foot {
    border-top: none;
    justify-content: flex-end;
    padding: 1.5rem 2rem;
    background: linear-gradient(to top, #f9f9f9, #fff);
    gap: 1rem;

    .body--dark & {
      background: linear-gradient(to top, #171923, #1a202c);
    }

    .button {
      min-width: 120px;
      font-weight: 600;
      transition: all 0.2s ease;

      &.is-primary {
        background: linear-gradient(135deg, #3e8ed0 0%, #4a90e2 100%);
        border: none;
        color: white;

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(62, 142, 208, 0.4);
        }

        &:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .body--dark & {
          background: linear-gradient(135deg, #4a5af8 0%, #6366f1 100%);

          &:hover:not(:disabled) {
            box-shadow: 0 8px 20px rgba(74, 90, 248, 0.4);
          }
        }
      }
    }
  }
}

:global(.body--dark) {
  .apple-relay-email-modal {
    background-color: #1a202c;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);

    .modal-card-head {
      background: linear-gradient(135deg, #4a5af8 0%, #6366f1 100%);
    }
  }
}

@media (max-width: $mobile) {
  .apple-relay-email-modal {
    width: 95%;
    margin: 10px auto;

    .modal-card-head {
      padding: 1.25rem;

      .modal-card-title {
        font-size: 1.15rem;
      }
    }

    .modal-card-body {
      padding: 1.5rem;
    }

    .modal-card-foot {
      padding: 1.25rem;
      flex-direction: column;

      .button {
        width: 100%;
        margin: 0 0 0.75rem !important;
      }

      .button:last-child {
        margin-bottom: 0 !important;
      }
    }
  }
}
</style>

<style>
.apple-relay-email-modal .modal-card-head .modal-card-title {
  color: white !important;
}
</style>
